import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import DescriptionContainer from '@/components/DescriptionList/DescriptionContainer';
import DescriptionRow from '@/components/DescriptionList/DescriptionRow';
import { H3, H4, H5 } from '@/components/Element/Heading';
import VerticalLeadFormSection from '@/components/LeadForm/VerticalLeadFormSection';
import MetaTags from '@/components/Head/MetaTags';
import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import { YEAR } from 'lib/constants';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Vehicle } from 'data/types';
import { Make } from 'data/types';
import { getMakeBySlug, getVehicleBySlug } from 'data/database';
import Image from 'next/legacy/image';
import { ProductJsonLd } from 'next-seo';
import { AiFillCalculator } from 'react-icons/ai';
import FullWidthFormSection from '@/components/LeadForm/FullWidthFormSection';
import Head from 'next/head';
// import ModelCalculator from '@/components/EVCalculator/ModelCalculator';

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking', // false or 'blocking'
  };
}

type StaticProps = {
  params: {
    make_slug: string;
    vehicle_slug: string;
  };
};

export async function getStaticProps({
  params: { make_slug, vehicle_slug },
}: StaticProps) {
  const make = await getMakeBySlug(make_slug);
  const vehicle = await getVehicleBySlug(vehicle_slug);
  console.log(vehicle);
  return { props: { make, vehicle } };
}
type Props = {
  make: Make;
  vehicle: Vehicle;
};

export default function Page({ make, vehicle }: Props) {
  const router = useRouter();
  const canonicalUrl = `https://energypal.com${router.asPath}`;

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  return (
    <article>
      <Head>
        <link rel="canonical" href={canonicalUrl.split('?')[0]} />
      </Head>
      <MetaTags
        title={`${vehicle.model} ${vehicle.trim} Electric Vehicle Guide`}
        description={`Learn about the ${vehicle.model} ${vehicle.trim}. Explore EV range, battery and more.`}
      />
      <ProductJsonLd
        productName={vehicle.model + ' ' + vehicle.trim}
        brand={make.company_name}
        manufacturerName={make.company_name}
        manufacturerLogo={make.webpPath}
        images={[vehicle?.webpPath, make.webpPath].filter(Boolean) as string[]}
        description={vehicle.model + ' ' + vehicle.trim}
        sku={vehicle.slug}
        offers={[
          {
            lowPrice: 15000,
            price: 35000,
            highPrice: 200000,
            url: `https://energypal.com/best-electric-vehicles/${make.slug}/${vehicle.slug}`,
            priceCurrency: 'USD',
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            priceValidUntil: '2024-01-01',
            seller: {
              '@type': 'Organization',
              name: 'EnergyPal',
              url: 'https://energypal.com',
            },
          },
        ]}
      />
      <SimpleCenteredHeaderSection
        dark
        supTitle={`Buyer's Guide ${YEAR}`}
        title={`${vehicle.model}`}
        subTitle="Electric Vehicle Datasheet"
      />
      <div className="hidden md:block">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Resources',
              href: '/solar-customer-resources',
            },
            {
              name: 'Best Electric Vehicles',
              href: '/best-electric-vehicles',
            },
            {
              name: `${make.company_name}`,
              href: `/best-electric-vehicles/${vehicle.make}`,
            },
            {
              name: `${vehicle.model}`,
              href: `/best-electric-vehicles/tesla/${vehicle.slug}`,
            },
          ]}
        />
      </div>
      <div className="md:hidden">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Resources',
              href: '/solar-customer-resources',
            },
            {
              name: 'Best Electric Vehicles',
              href: '/best-electric-vehicles',
            },
            {
              name: `${make.company_name}`,
              href: `/best-electric-vehicles/${vehicle.make}`,
            },
          ]}
        />
      </div>
      <CenteredContentSection>
        <div className="w-[300px] md:w-[500px] min-h-fit mx-auto rounded-md">
          <Image
            src={vehicle.webpPath as string}
            alt={vehicle.name}
            width={600}
            height={350}
            className="rounded-md"
            layout="responsive"
          />
        </div>
        {/* <ModelCalculator model={vehicle.model || ''} /> */}
        <H3>{vehicle.name} Overview</H3>

        <p>
          The {vehicle.name} represents the forefront of electric vehicle (EV)
          technology, combining impressive performance with environmental
          sustainability. As the world shifts towards greener alternatives, the
          synergy between EVs and solar power has emerged as a powerful solution
          to reduce carbon footprints and embrace renewable energy sources. This
          guide explores how to power the {vehicle.name} using solar panels,
          ensuring your drive is not just efficient but also eco-friendly.
        </p>
        <p>
          The {vehicle.name} {vehicle.trim?.toString()} stands out with its
          remarkable efficiency of {vehicle.efficiency?.toString()} kWh/100
          miles and a substantial range of {vehicle.range_epa?.toString()} miles
          on a single charge. Manufactured in {vehicle.manufactured?.toString()}
          , this {vehicle.body?.toString()} features a{' '}
          {vehicle.motor_type?.toString()} motor, delivering{' '}
          {vehicle.engine_power?.toString()} of power and{' '}
          {vehicle.engine_torque?.toString()} of torque. Its acceleration from
          0-60 mph in just {vehicle.accel_0_60_mph?.toString()} seconds and a
          top speed of {vehicle.top_speed?.toString()} highlight the{' '}
          {vehicle.model?.toString()}&apos;s exceptional performance
          capabilities.
        </p>
        <p>
          Here are the full specifications for the {vehicle.name} {vehicle.trim}
          :
        </p>
        <DescriptionContainer>
          <DescriptionRow label="Model">{vehicle.model}</DescriptionRow>
          <DescriptionRow label="Trim">{vehicle.trim}</DescriptionRow>
          <DescriptionRow label="Brand">
            <Link href={`/best-electric-vehicles/${vehicle.make}`}>
              {make.company_name}
            </Link>
          </DescriptionRow>
          <DescriptionRow label="Year">
            {vehicle.year?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Battery Size">
            {vehicle.battery_capacity?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Range EPA">
            {vehicle.range_epa?.toString()}
          </DescriptionRow>
          <DescriptionRow
            label="Vehicle
          Efficiency"
          >
            {vehicle.efficiency?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Manufactured">
            {vehicle.manufactured?.toString()}
          </DescriptionRow>
          <div className="bg-primary-400 rounded-md p-4 flex flex-col items-center justify-center text-center mt-3">
            <div className="flex flex-col items-center mb-4 ">
              <AiFillCalculator className="text-white text-5xl" />
              <H5 className="text-white font-medium py-2">
                Find out how many solar panels you need to power the{' '}
                {vehicle.model}
              </H5>
            </div>
            <Link href={'/best-electric-vehicles/calculator'} legacyBehavior>
              <button className="bg-primary-500 hover:bg-primary-600 text-white rounded-md px-4 py-2">
                Calculate
              </button>
            </Link>
          </div>

          {/* <Center>
            <p>
              The {vehicle.model} {vehicle.trim} has a 0-60 mph acceleration of{' '}
              {vehicle.accel_0_60_mph?.toString()} and a top speed of{' '}
              {vehicle.top_speed?.toString()} mph. It is equipped with a motor
              that delivers {vehicle.engine_power?.toString()} of power and{' '}
              {vehicle.engine_torque?.toString()} pound-feet (Newton meters) of
              total torque. The drive type is {vehicle.drive_type?.toString()}{' '}
              and the vehicle&lsquo;s efficiency is{' '}
              {vehicle.efficiency?.toString()}. The motor type is a{' '}
              {vehicle.motor_type?.toString()} design, which is known for its
              reliability and efficiency.
            </p>
          </Center> */}
        </DescriptionContainer>
        <H4>Vehicle Performance</H4>
        <DescriptionContainer>
          <DescriptionRow label="Vehicle Accleration (0-60 mph)">
            {vehicle.accel_0_60_mph?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Top Speed">
            {vehicle.top_speed?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Engine Power">
            {vehicle.engine_power?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Total Torque">
            {vehicle.engine_torque?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Drive Type">
            {vehicle.drive_type?.toString()}
          </DescriptionRow>
          <DescriptionRow
            label="Vehicle
          Efficiency"
          >
            {vehicle.efficiency?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Motor Type">
            {vehicle.motor_type?.toString()}
          </DescriptionRow>
        </DescriptionContainer>
        <H4>Battery</H4>
        <DescriptionContainer>
          <DescriptionRow label="Capacity">
            {vehicle.battery_pack_capacity?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Range EPA">
            {vehicle.range_epa?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Range WLPT">
            {vehicle.range_wlpt?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Range NEDC">
            {vehicle.range_nedc?.toString()}
          </DescriptionRow>
        </DescriptionContainer>

        <H3>The Role of Solar Power in EV Charging</H3>
        <p>
          Integrating solar power with electric vehicle charging presents a
          sustainable path forward. Solar panels convert sunlight into
          electricity, offering a clean, renewable source that can significantly
          reduce reliance on fossil fuels. For EV owners, particularly those
          with a {vehicle.name}, solar energy can provide a cost-effective,
          eco-friendly way to charge their vehicles, contributing to reduced
          electricity bills and a smaller carbon footprint.
        </p>
        <H4> Charging info for {vehicle.name} </H4>
        <DescriptionContainer>
          <DescriptionRow label="Charge Port Type">
            {vehicle.charge_port?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Max charging power (AC)">
            {vehicle.max_charging_power_ac?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Max charging power (DC)">
            {vehicle.max_charging_power_ad?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Average charging speed (DC)">
            {vehicle.avg_charging_speed_dc?.toString()}
          </DescriptionRow>
        </DescriptionContainer>
        <H4>Calculating Solar Panel Needs for the {vehicle.name}</H4>
        <p>
          To determine the number of solar panels required to power the{' '}
          {vehicle.name}, consider its energy consumption of{' '}
          {vehicle.efficiency?.toString()} kWh/100 miles and a{' '}
          {vehicle.battery_pack_capacity?.toString()} kWh battery capacity. The
          calculation involves assessing your daily or monthly mileage, the
          efficiency of the solar panels you intend to install, and the average
          sunlight hours your location receives. You can check how many panel
          using our{' '}
          <Link href={'/best-electric-vehicles/calculator'}>
            EV solar charging calculator.
          </Link>
        </p>
        <H4>Installation and Cost Considerations</H4>
        <p>
          Installing solar panels to power your {vehicle.name} involves several
          considerations, including the choice of panels, the capacity of your
          solar system, and the specifics of your installation site. Costs can
          vary based on these factors, but solar incentives and rebates can make
          the investment more affordable. Financing options, such as $0 down
          leases, are available to help manage upfront costs, making solar power
          accessible to a broader audience.
        </p>
      </CenteredContentSection>
      <div className="w-3/4 mx-auto">
        <FullWidthFormSection
          title="Getting an EV? We'll help you power your car and
            home."
        />
      </div>

      <CenteredContentSection>
        <H4>Technical Specifications</H4>
        <DescriptionContainer>
          <DescriptionRow label="Body Type">
            {vehicle.body?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Weight">
            {vehicle.curb_weight?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Dimensions">
            {vehicle.dimensions_lxwxh?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Cargo Volume">
            {vehicle.cargo_volume?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Wheelbase">
            {vehicle.wheelbase?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Number of seats">
            {vehicle.number_of_seats?.toString()}
          </DescriptionRow>
          <DescriptionRow label="Towing Capacity">
            {vehicle.towing_capacity?.toString()}
          </DescriptionRow>
        </DescriptionContainer>
      </CenteredContentSection>
      <VerticalLeadFormSection />
    </article>
  );
}
